import type { BlockPendingDeletionState } from '@blocksuite/affine-model';
import { html, nothing, type TemplateResult } from 'lit';

import type { GuardedTrashBlockKind } from './trashcan-utils.js';

export type BlockPendingDeletionText =
  | 'queued'
  | 'confirming'
  | 'unconfirmed'
  | 'cancel'
  | 'retry';

/**
 * The line above a block whose trash is queued and that stays visible: the
 * phase, an optional usage marker and the one action of the phase (cancel
 * while queued, check again when unconfirmed, none while the server
 * confirms). Tables and automations, on page and canvas, render the same line.
 */
export function renderBlockPendingDeletionNotice(
  state: BlockPendingDeletionState,
  {
    kind,
    text,
    classes,
    usage = nothing,
    onCancel,
    onRetry,
  }: {
    kind: GuardedTrashBlockKind;
    text: (key: BlockPendingDeletionText) => string;
    classes: { notice: string; message: string; button: string };
    usage?: TemplateResult | typeof nothing;
    onCancel: () => void;
    onRetry: () => void;
  }
) {
  if (state.kind !== 'visible') return nothing;
  const act = (run: () => void) => (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    run();
  };
  const action =
    state.phase === 'queued'
      ? { label: text('cancel'), run: onCancel, testId: 'cancel' }
      : state.phase === 'unconfirmed'
        ? { label: text('retry'), run: onRetry, testId: 'retry' }
        : null;
  return html`
    <div
      class=${classes.notice}
      contenteditable="false"
      role="status"
      data-phase=${state.phase}
      data-testid="${kind}-deletion-pending"
    >
      <span class=${classes.message}>${text(state.phase)}</span>
      ${usage}
      ${action
        ? html`<button
            type="button"
            class=${classes.button}
            data-testid="${kind}-deletion-pending-${action.testId}"
            @click=${act(action.run)}
          >
            ${action.label}
          </button>`
        : nothing}
    </div>
  `;
}
